import {
  Board,
  pageFailure,
  QuoteState,
} from "../../../../../reducers/uploadedBoard";
import useSendBoardEvent, { BoardEvent } from "../state-machine/sendBoardEvent";
import { useShoppingCartSummary } from "../../shoppingCart";
import { useCustomConfirm, useCustomOptions } from "../../../alerts";
import { useCallback, useContext } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import { ButtonVariants } from "../../../../../components/common/Button/constants";

export const useShouldAskToKeepQuote = () => {
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );
  return useCallback(
    (board: Board) => {
      return (
        isAdminOrEngineer &&
        board.quote.requested_custom_quote &&
        board.quote.total !== null &&
        board.quote.state !== QuoteState.ORDER_RECEIVED
      );
    },
    [isAdminOrEngineer],
  );
};

export const useGetAlertMessage = () => {
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );
  const shouldAskToKeepQuote = useShouldAskToKeepQuote();

  return useCallback(
    (board: Board) => {
      if (board.quote.state === QuoteState.ORDER_RECEIVED) {
        return null;
      }
      if (shouldAskToKeepQuote(board)) {
        return null;
      }

      if (board.quote.state == QuoteState.IN_SHOPPING_CART) {
        if (board.quote.requested_custom_quote) {
          return `Editing this board will remove it from your cart. You will need to request a new custom quote from a FlashPCB engineer before adding to your cart again.\n\nContinue?`;
        }
        return "Editing this board will remove it from your cart.\n\nContinue?";
      } else if (board.quote.requested_custom_quote) {
        if (board.quote.total === null) {
          if (!isAdminOrEngineer) {
            return `Editing this board will cancel the custom quote request.\n\nContinue?`;
          }
        } else {
          return `Editing this board will revoke your custom quote. You might need to request a new quote from a FlashPCB engineer to see a price again.\n\nContinue?`;
        }
      }
      return null;
    },
    [isAdminOrEngineer, shouldAskToKeepQuote],
  );
};

const getKeepQuoteMessage = (board: Board) => {
  const quote =
    board.quote.total === null ? "custom quote request" : "custom quote";
  const cartWarning =
    board.quote.state === QuoteState.IN_SHOPPING_CART
      ? " Recalculating the quote will remove the board from the customer's cart."
      : "";
  return `This board has a ${quote}. Keep it as it is, or update the quote for this change?${cartWarning}`;
};

export function useSendUpdateEvent(undoUpdate?: () => void) {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const boardContext = useAppSelector((state) =>
    boardId ? state.uploadedBoard.boards[boardId] : null,
  );
  const quoteState = boardContext?.board?.quote.state;
  const board = boardContext?.board ?? null;
  const dispatch = useAppDispatch();
  const errorHandler = useCallback(() => {
    if (boardId) {
      dispatch(pageFailure({ boardId }));
    }
  }, [dispatch, boardId]);
  const { sendBoardEvent, loading } = useSendBoardEvent(errorHandler);
  const { refreshShoppingCartSummary } = useShoppingCartSummary();
  const customConfirm = useCustomConfirm();
  const customOptions = useCustomOptions();
  const getAlertMessage = useGetAlertMessage();
  const shouldAskToKeepQuote = useShouldAskToKeepQuote();

  const performUpdate = useCallback(
    async (event: BoardEvent) => {
      if (boardId) {
        await sendBoardEvent(boardId, event).then(() => {
          if (quoteState == QuoteState.IN_SHOPPING_CART) {
            void refreshShoppingCartSummary();
          }
        });
      }
    },
    [boardId, quoteState, refreshShoppingCartSummary, sendBoardEvent],
  );

  const sendUpdateEvent = useCallback(
    async (event: BoardEvent, onSuccess?: () => void) => {
      if (board !== null) {
        const confirmationMessage = getAlertMessage(board);

        if (shouldAskToKeepQuote(board)) {
          customOptions(
            getKeepQuoteMessage(board),
            [
              {
                text: "Keep quote",
                variant: ButtonVariants.SECONDARY,
                onSelect: async () => {
                  await performUpdate({
                    ...event,
                    update: { ...event.update, keep_quote: true },
                  });
                  onSuccess?.();
                },
              },
              {
                text: "Recalculate quote",
                variant: ButtonVariants.PRIMARY,
                onSelect: async () => {
                  await performUpdate(event);
                  onSuccess?.();
                },
              },
            ],
            () => {
              undoUpdate?.();
            },
          );
        } else if (confirmationMessage !== null) {
          customConfirm(
            confirmationMessage,
            async () => {
              await performUpdate(event);
              onSuccess?.();
            },
            () => {
              undoUpdate?.();
            },
          );
        } else {
          await performUpdate(event);
          onSuccess?.();
        }
      }
    },
    [
      board,
      customConfirm,
      customOptions,
      undoUpdate,
      performUpdate,
      getAlertMessage,
      shouldAskToKeepQuote,
    ],
  );

  return { sendUpdateEvent, loading };
}
